import { useCallback, useEffect, useState } from "react";
import { History, Send } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { Card, Input, Label, Select, Textarea } from "./_shared";

// ---------------------------------------------------------------------------
// Broadcast Tab — owner-only one-to-many email blasts. Audience filter, preview
// before send, and a safe "send to me only" test path. History logged in DB.
// ---------------------------------------------------------------------------
export const AUDIENCE_OPTIONS = [
  { key: "test",          label: "Test → kun til meg",      hint: "Anbefalt før ekte utsendelse" },
  { key: "all",           label: "Alle brukere",            hint: "Inkluderer kansellerte og utløpte" },
  { key: "paying",        label: "Aktive + prøveperiode",   hint: "Alle som har et aktivt abonnement" },
  { key: "active_paying", label: "Kun aktive (uten trial)", hint: "Betalende kunder" },
  { key: "trialing",      label: "Kun prøveperiode",        hint: "Bra for trial-konvertering" },
  { key: "canceled",      label: "Kansellerte / utløpte",   hint: "Vinn dem tilbake" },
];

export function BroadcastTab() {
  const { apiFetch } = useAuth();
  const [audience, setAudience] = useState("test");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [testTo, setTestTo] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const loadHistory = useCallback(async () => {
    try { const d = await apiFetch("/api/admin/broadcast/history"); setHistory(d.items || []); }
    catch (e) { log.warn("[BroadcastTab] history load failed:", e?.message || e); }
  }, [apiFetch]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const buildPayload = () => ({
    subject: subject.trim(),
    body: body.trim(),
    audience,
    cta_label: ctaLabel.trim() || null,
    cta_url:   ctaUrl.trim() || null,
    test_to:   audience === "test" ? testTo.trim() : null,
  });

  const preview = async () => {
    if (!subject.trim() || !body.trim()) { setErr("Emne og melding må fylles ut først."); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const d = await apiFetch("/api/admin/broadcast/preview", { method: "POST", body: JSON.stringify(buildPayload()) });
      setPreviewHtml(d.html);
      setRecipientCount(d.recipient_count);
    } catch (e) { setErr(e?.message || "Preview failed"); }
    finally { setBusy(false); }
  };

  const send = async (dryRun = false) => {
    if (!subject.trim() || !body.trim()) { setErr("Emne og melding må fylles ut først."); return; }
    if (audience === "test" && !testTo.trim()) { setErr("Skriv inn en test-e-postadresse."); return; }
    const recipients = recipientCount ?? "ukjent";
    if (!dryRun && audience !== "test" && !window.confirm(`Send ekte e-post til ${recipients} mottakere? Dette kan ikke angres.`)) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const d = await apiFetch("/api/admin/broadcast/send", {
        method: "POST",
        body: JSON.stringify({ ...buildPayload(), dry_run: dryRun }),
      });
      setResult(d);
      await loadHistory();
    } catch (e) { setErr(e?.message || "Send failed"); }
    finally { setBusy(false); }
  };

  const audienceOpt = AUDIENCE_OPTIONS.find((a) => a.key === audience);

  return (
    <div className="space-y-4" data-testid="broadcast-root">
      <Card title="EMAIL BROADCAST"
            subtitle="Send announcements, price changes, deal launches. Recipients are personalized with their first name. History is logged."
            accent="#0B6CF5" testid="broadcast-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: compose */}
          <div>
            <Label>Mottakere</Label>
            <Select value={audience} onChange={(e) => { setAudience(e.target.value); setRecipientCount(null); setPreviewHtml(""); }} data-testid="broadcast-audience">
              {AUDIENCE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </Select>
            <div className="text-[11px] text-[var(--srs-ink-faint)] mt-1">{audienceOpt?.hint}</div>

            {audience === "test" && (
              <>
                <Label>Test-e-post</Label>
                <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="meg@europilot.no" data-testid="broadcast-test-to" />
              </>
            )}

            <Label>Emne</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Vi har lansert årlig betaling" maxLength={160} data-testid="broadcast-subject" />

            <Label>Melding (tomme linjer = nytt avsnitt)</Label>
            <Textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} maxLength={6000}
                      placeholder={"Hei!\n\nNå kan du betale årlig for EuroPilot og spare ~25%.\n\nDet er ingen forskjell på funksjonene, bare på prisen."}
                      data-testid="broadcast-body" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CTA-tekst (valgfritt)</Label>
                <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} placeholder="Se nytt abonnement" data-testid="broadcast-cta-label" />
              </div>
              <div>
                <Label>CTA-URL</Label>
                <Input type="url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://europilot.no/account" data-testid="broadcast-cta-url" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              <button onClick={preview} disabled={busy} data-testid="broadcast-preview-btn"
                      className="px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-bold border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] rounded-full disabled:opacity-50">
                Forhåndsvis
              </button>
              <button onClick={() => send(true)} disabled={busy} data-testid="broadcast-dryrun-btn"
                      className="px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-bold border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] rounded-full disabled:opacity-50">
                Tørrtest (ingen e-post)
              </button>
              <button onClick={() => send(false)} disabled={busy} data-testid="broadcast-send-btn"
                      className="inline-flex items-center gap-2 px-5 py-2 bg-[#0B6CF5] text-white text-[10px] font-bold tracking-[0.18em] uppercase rounded-full disabled:opacity-50">
                <Send className="w-3 h-3" />
                {busy ? "Sender…" : audience === "test" ? "Send test" : "Send ekte"}
              </button>
            </div>

            {err && <div className="text-xs text-[#EF4444] mt-3" data-testid="broadcast-err">{err}</div>}
            {result && (
              <div className="text-[11px] mt-3 p-3 border border-[var(--srs-line)] rounded-lg bg-[var(--srs-surface-2)]" data-testid="broadcast-result">
                {result.dry_run ? (
                  <>🧪 Tørrtest fullført: <strong>{result.recipient_count}</strong> ville fått e-posten.</>
                ) : (
                  <>✅ Sendt til <strong>{result.sent}</strong> av {result.recipient_count} · feilet: {result.failed}</>
                )}
              </div>
            )}
          </div>

          {/* Right: preview */}
          <div>
            <Label>Forhåndsvisning</Label>
            {recipientCount !== null && (
              <div className="text-[11px] text-[var(--srs-ink-dim)] mb-2" data-testid="broadcast-recipient-count">
                {recipientCount} {recipientCount === 1 ? "mottaker" : "mottakere"} matcher filteret
              </div>
            )}
            {previewHtml ? (
              <iframe title="Email preview" srcDoc={previewHtml}
                      data-testid="broadcast-preview-iframe"
                      style={{ width: "100%", minHeight: 480, border: "1px solid var(--srs-line)", borderRadius: 10, background: "#F0F6FB" }} />
            ) : (
              <div className="border border-dashed border-[var(--srs-line)] rounded-lg p-8 text-center text-[11px] text-[var(--srs-ink-faint)]" data-testid="broadcast-preview-empty">
                Trykk "Forhåndsvis" for å rendre e-posten her.
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card title="HISTORIKK"
            subtitle={`${history.length} broadcasts logged.`}
            accent="#0B6CF5" testid="broadcast-history-card">
        {history.length === 0 ? (
          <div className="text-[11px] text-[var(--srs-ink-faint)] py-4 text-center">Ingen utsendelser enda.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {history.map((h) => (
              <div key={h.id} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-3 rounded-lg" data-testid={`broadcast-history-${h.id}`}>
                <div className="flex flex-wrap justify-between items-center gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[var(--srs-ink)] text-sm">{h.subject}</span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase font-bold bg-[var(--srs-surface)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">{h.audience}</span>
                    {h.dry_run && <span className="px-2 py-0.5 text-[9px] tracking-[0.18em] uppercase font-bold bg-[#FFE9DC] text-[var(--srs-sand-deep)]">dry run</span>}
                  </div>
                  <span className="text-[10px] text-[var(--srs-ink-faint)] font-mono">{new Date(h.started_at).toLocaleString()}</span>
                </div>
                <div className="text-[11px] text-[var(--srs-ink-dim)]">
                  ✓ {h.sent_count} sent · ✗ {h.failed_count} failed · {h.recipient_count} total · by {h.actor}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Proposed Methods Tab — review queue for user-claimed payment methods that
// the server-side verifier couldn't corroborate. Owner approves (graduates to
// trusted) or rejects (suppresses + blocks future re-reports via
// false_positive_methods). Auto-graduates at 3 distinct reports.
// ---------------------------------------------------------------------------
export const PROPOSAL_THRESHOLD = 3;

